import { Link } from "@alpacahq/ui/old";

import type { FAQItem } from "~/v2/components/common/FAQ";

/**
 * Shared ACH FAQ content. Also consumed by the deposit/ach and withdraw/ach
 * pages via the `link/ach` barrel, so it lives in its own module instead of the
 * connector to keep the import path stable across the page split.
 */
export const ACH_FAQ: FAQItem[] = [
  {
    question: "What is Plaid?",
    answer: (
      <>
        Alpaca uses Plaid to verify your bank account information and allow you
        to link your bank account.
      </>
    ),
  },
  {
    question: "What is the deposit / withdrawal limit?",
    answer: (
      <>
        We have a deposit / withdrawal limit of $50,000 per transfer. If you
        would like to transfer an amount exceeding this limit, please email{" "}
        <Link href="mailto:support@alpaca.markets">support@alpaca.markets</Link>
      </>
    ),
  },
  {
    question: "How long does it take to deposit / withdraw in my account?",
    answer: <>1-3 business days once an ACH transfer is initiated</>,
  },
  {
    question: "Why are my funds not available for withdrawal?",
    answer: (
      <>
        Funds from sell transactions take one business day to settle. Withdrawal
        requests must be submitted only after the funds are fully settled in
        your account.
        <br />
        <br />
        Additionally, deposited funds are subject to a mandatory 7 business day
        settlement period. During this time, the funds cannot be withdrawn.
      </>
    ),
  },
  {
    question: "What is Instant ACH Funding?",
    answer: (
      <>
        U.S. customers can trade instantly on ACH deposits up to $3,000 total,
        when the linked bank account matches Alpaca&#39;s records. The eligible
        deposit amount appears immediately in buying power.
        <br />
        <br />
        Tier 1 (new users): up to $1,000 per transfer
        <br />
        <br />
        Tier 2 (established users with $1,000+ in completed ACH history): up to
        $3,000 per transfer
        <br />
        <br />
        Only one instant ACH deposit is allowed at a time until it clears, and
        instant ACH funds cannot be withdrawn until the deposit has fully
        settled.
        <br />
        <br />
        Please note: Banks linked as micro deposits are not Instant ACH
        eligible.
      </>
    ),
  },
  {
    question: "Why can't I find my bank on the list?",
    answer: (
      <>
        Your bank doesn&#39;t support Plaid linking, but you can still transfer
        funds via wire transfer.
      </>
    ),
  },
  {
    question: "Can I connect my Wise bank account?",
    answer: (
      <>
        Users can connect their Wise bank account; however, the account must be
        in USD to transfer funds.
      </>
    ),
  },
  {
    question: "Why is my bank link rejected / canceled?",
    answer: (
      <>
        Typically your authentication (for example, your passwords) at your bank
        changed. Please try relinking your bank account.
        <br />
        <br />
        Additionally, please ensure the bank account you link matches the name
        on your Alpaca brokerage account, as mismatched ownership is not
        supported.
        <br />
        <br />
        For further troubleshooting steps, please see{" "}
        <a
          href="https://plaid.com/trouble-connecting/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Plaid Troubleshooting
        </a>
      </>
    ),
  },
  {
    question: "Why was my ACH returned?",
    answer: (
      <>
        ACH transfer requests may be denied if there are insufficient settled
        funds, if the bank link is new and requires verification, or if the bank
        account name does not match your Alpaca brokerage account.
        <br />
        <br />
        If you have any questions, please contact your bank directly.
      </>
    ),
  },
  {
    question: "What if I cannot link my account through Plaid successfully?",
    answer: (
      <>
        Make sure your browser is up to date, pop-ups are enabled, and
        you&#39;re not using full-screen mode while linking.
        <br />
        <br />
        For further troubleshooting steps, please see{" "}
        <a
          href="https://plaid.com/trouble-connecting/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Plaid Troubleshooting
        </a>
      </>
    ),
  },
];
